import type { MedicationDose } from './to-medication-order';

/**
 * The dictated sig a pick from the Add-Order drawer may open its dose form with, or nothing.
 *
 * WHY THIS IS A FUNCTION OF ITS OWN. It used to be an inline check in `encounter-shell-page.tsx`,
 * and the check was right about the danger and wrong about the catalogue. The danger (Codex,
 * PROD-1972 follow-up): a clinician lands in the seeded "paracetamol" search, retypes, picks
 * ibuprofen, and the ibuprofen form opens with paracetamol's 500 mg three-times-daily sig. So the
 * dictated sig was bound to the drug by NAME: the picked row's name had to contain the dictated
 * name.
 *
 * THE CATALOGUE DEFEATS THAT RULE. A DDC catalogue row is named by its BRAND ("ADOL 500MG …"). The
 * generic lives in the row's long description, which the server searches but never returns
 * (`MedicationRepository.GetAllMedications`, Scrips.Master: `Display LIKE %q%` OR every token in
 * `LongDescription`). So the seeded search for "paracetamol" lists ADOL, PANADOL and friends, the
 * clinician picks one, the name check fails, and the dose form opens blank after the dose was said
 * aloud. That is what Samer saw on 1 Oct: the sig never reached the order.
 *
 * THE SECOND RULE, behind the `frameV2` switch: a row picked while the drawer's query is STILL the
 * seeded query, unedited, AND its results have settled for that query, was returned by a search for
 * the dictated name, so it names that drug somewhere in its description and may carry its sig.
 * Retyping anything breaks the binding, which is the ibuprofen case the first rule exists for.
 * Switching category tabs clears the query, so that breaks it too. The rail reports an EMPTY query
 * while results are still loading (`ap-search-rail.tsx`), because the rows on screen then may still
 * be the previous query's (React Query keeps them as placeholder data): picking "BRUFEN" from a
 * stale "ibuprofen" list while the box already reads "paracetamol" must not bind (Codex, P1).
 *
 * A SEARCH MATCH IS STILL NOT IDENTITY. `LongDescription LIKE %paracetamol%` also matches a
 * combination product, and every token of a multi-word name may appear in an unrelated row. The
 * NAME rule has the same hole: "Paracetamol + Codeine" contains "paracetamol" (Codex, second pass).
 * So with the switch ON every carried sig, by either rule, is marked `dictatedFor`, and the dose
 * form says, above the fields it filled, which drug the words were spoken for and asks the
 * physician to check them against the product she picked. With the switch OFF the name rule marks
 * nothing, exactly as before.
 *
 * WHAT IT NEVER DOES. It never fills a form by itself and never writes an order. It decides only
 * whether the form the clinician opens starts with the words she said, which she then confirms or
 * corrects before anything is pended.
 */
export interface DictatedSearchSeed {
  category: string;
  query: string;
  dictatedDose?: MedicationDose;
}

export interface DictatedDoseForPickInput {
  /** The ambient pre-fill the drawer was opened with. Absent for a drawer the clinician opened. */
  seed: DictatedSearchSeed | null | undefined;
  /** The category the pick came from. */
  category: string;
  /** The raw row the clinician picked. */
  pickedItem: unknown;
  /** The drawer's search text at the moment of the pick. */
  queryAtPick?: string;
  /**
   * `frameV2`: a row from the UNEDITED, settled seeded search also carries the sig, and every carried
   * sig is marked `dictatedFor` (see the header). OFF: the name rule alone, unmarked.
   */
  switchOn: boolean;
}

const lower = (value: string | undefined | null): string => (value ?? '').trim().toLowerCase();

function pickedNameOf(item: unknown): string {
  const src = item as { name?: string; display?: string; displayName?: string } | undefined;
  return lower(src?.name ?? src?.display ?? src?.displayName);
}

export function dictatedDoseForPick({
  seed,
  category,
  pickedItem,
  queryAtPick,
  switchOn,
}: DictatedDoseForPickInput): MedicationDose | undefined {
  if (!seed?.dictatedDose || seed.category !== category) return undefined;
  const seededName = lower(seed.query);
  if (!seededName) return undefined;
  // ON: whatever rule carries it, the sig says where it came from, because neither a name nor a
  // search match is proof of identity (see the header).
  const carried = switchOn ? { ...seed.dictatedDose, dictatedFor: seed.query.trim() } : seed.dictatedDose;
  // Rule 1, both switch states: the picked row names the dictated drug.
  if (pickedNameOf(pickedItem).includes(seededName)) return carried;
  // Rule 2, switch ON only: the row came from the seeded search, unedited AND settled.
  if (switchOn && lower(queryAtPick) === seededName) return carried;
  return undefined;
}
